import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { flowsheetUrl } from './api';
import { eddFromArt, eddFromLmp, gaDays, redatingDifference } from './calculations';
import { replayIdentity } from './EventHistory';
import type { PracticeConfiguration } from './rules';
export function DatingReview({
  episodeId,
  encounterId,
  subjectId,
  edd,
  configuration,
  disabled,
}: {
  episodeId: string;
  encounterId: string;
  subjectId: string;
  edd: string | null;
  configuration?: PracticeConfiguration;
  disabled: boolean;
}) {
  const { organization } = useAuth();
  const { permissions } = usePermissions();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false),
    [method, setMethod] = useState('LMP'),
    [date, setDate] = useState(''),
    [scan, setScan] = useState(''),
    [cycle, setCycle] = useState('28'),
    [embryo, setEmbryo] = useState('5'),
    [reason, setReason] = useState(''),
    [reviewed, setReviewed] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [attempted, setAttempted] = useState(false);
  const request = useRef<Record<string, unknown> | null>(null);
  if (!permissions.includes('create_clinical_event')) return null;
  const proposed =
    method === 'LMP' ? eddFromLmp(date, Number(cycle)) : method === 'ART' ? eddFromArt(date, Number(embryo)) : date;
  const refs =
    configuration?.clinically_reviewed && configuration.clinical_references?.enabled
      ? configuration.clinical_references
      : null;
  const ga = gaDays(scan, edd);
  const discrepancy =
    edd && proposed && ga !== null && refs ? redatingDifference(edd, proposed, ga, refs.redating_bands) : null;
  const ultrasoundBlocked = method === 'ULTRASOUND' && !!edd && (!discrepancy || !discrepancy.exceedsThreshold);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!reviewed || !proposed || ultrasoundBlocked || !organization || disabled) return;
    setBusy(true);
    setMessage('');
    setAttempted(true);
    if (!request.current)
      request.current = {
        ...replayIdentity(),
        encounter_id: encounterId,
        subject_id: subjectId,
        expected_edd: edd,
        method,
        proposed_edd: proposed,
        reason,
        lmp: method === 'LMP' ? date : null,
        cycle_days: Number(cycle),
        transfer_date: method === 'ART' ? date : null,
        embryo_age_days: method === 'ART' ? Number(embryo) : null,
        scan_date: method === 'ULTRASOUND' ? scan || null : null,
        configuration_version: configuration?.version || null,
      };
    try {
      const result = (
        await clientApi.post<{ edd: string }>(
          `${flowsheetUrl(organization.organization_id)}/episodes/${episodeId}/dating`,
          request.current,
        )
      ).data;
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['pregnancies', organization.organization_id] }),
        cache.invalidateQueries({ queryKey: ['clinical-events', organization.organization_id, episodeId] }),
      ]);
      setMessage(`Dating updated to ${result.edd}; prior EDD and rationale retained in history.`);
      setOpen(false);
      request.current = null;
    } catch {
      setMessage(
        'Dating result could not be confirmed. Your reviewed request is retained for safe retry. If dating changed elsewhere, close and reload before a new review.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="flex flex-col gap-3">
      <Button
        type="button"
        variant="outline"
        disabled={disabled || busy}
        onClick={() => {
          if (request.current) {
            setOpen(true);
            return;
          }
          setOpen(true);
          setReviewed(false);
          setAttempted(false);
          request.current = null;
          setMessage('');
        }}>
        Review pregnancy dating
      </Button>
      {message && <p role="status">{message}</p>}
      {open && (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4 rounded border p-4">
          <p>
            Current EDD: {edd ?? 'Not recorded'}. This changes the linked pregnancy EDD only after explicit confirmation
            and retains a dating audit event.
          </p>
          <fieldset disabled={busy || attempted} className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel>Dating basis</FieldLabel>
              <Select
                value={method}
                onValueChange={(v) => {
                  setMethod(v);
                  setReviewed(false);
                }}>
                <SelectTrigger aria-label="Dating basis">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {['LMP', 'ART', 'ULTRASOUND', 'CLINICAL'].map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="dating-date">
                {method === 'LMP' ? 'LMP date' : method === 'ART' ? 'Embryo transfer date' : 'Proposed EDD'}
              </FieldLabel>
              <Input
                id="dating-date"
                type="date"
                required
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setReviewed(false);
                }}
              />
            </Field>
            {method === 'LMP' && (
              <Field>
                <FieldLabel htmlFor="cycle-days">Cycle length (days)</FieldLabel>
                <Input
                  id="cycle-days"
                  type="number"
                  min={15}
                  max={60}
                  required
                  value={cycle}
                  onChange={(e) => {
                    setCycle(e.target.value);
                    setReviewed(false);
                  }}
                />
              </Field>
            )}
            {method === 'ART' && (
              <Field>
                <FieldLabel htmlFor="embryo-days">Embryo age at transfer (days)</FieldLabel>
                <Input
                  id="embryo-days"
                  type="number"
                  min={0}
                  max={7}
                  required
                  value={embryo}
                  onChange={(e) => {
                    setEmbryo(e.target.value);
                    setReviewed(false);
                  }}
                />
              </Field>
            )}
            {method === 'ULTRASOUND' && (
              <Field>
                <FieldLabel htmlFor="scan-date">Ultrasound date</FieldLabel>
                <Input
                  id="scan-date"
                  type="date"
                  required
                  value={scan}
                  onChange={(e) => {
                    setScan(e.target.value);
                    setReviewed(false);
                  }}
                />
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="dating-rationale">Clinical rationale and source</FieldLabel>
              <Textarea
                id="dating-rationale"
                required
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReviewed(false);
                }}
              />
            </Field>
          </fieldset>
          <p>Proposed EDD: {proposed || 'Enter valid dates'}</p>
          {method === 'ULTRASOUND' && edd && (
            <p>
              {discrepancy
                ? `Discrepancy ${discrepancy.difference} days; reviewed threshold greater than ${refs?.redating_bands.find((b) => ga !== null && ga <= b.maxDays)?.differenceDays} days. ${discrepancy.exceedsThreshold ? 'Threshold exceeded.' : 'Threshold not exceeded. Clinical override requires Clinical basis and rationale.'}`
                : 'Reviewed dating references and scan date are required.'}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Checkbox
              id="dating-confirmed"
              checked={reviewed}
              disabled={busy || attempted}
              onCheckedChange={(v) => setReviewed(v === true)}
            />
            <label htmlFor="dating-confirmed">I reviewed the dating evidence and confirm this EDD change.</label>
          </div>
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={disabled || busy || !reviewed || !reason.trim() || !proposed || ultrasoundBlocked}>
              {attempted ? 'Retry reviewed dating change' : 'Apply reviewed dating change'}
            </Button>
            <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Close review
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
